import CircularProgress from '@jimmyboys/liita-ui/dist/components/CircularProgress'
import { useGlobal } from '@jimmyboys/liita-ui/dist/components/GlobalContext'
import HStack from '@jimmyboys/liita-ui/dist/components/HStack'
import VStack from '@jimmyboys/liita-ui/dist/components/VStack'
import ExpandableWidget from 'components/ExpandableWidget/ExpandableWidget'
import moment from 'moment'
import { Link, useParams } from 'react-router-dom'
import { parseDate } from 'models/utils'

import styles from './widgets.module.sass'
import { computeDisabilityDaysSummaryNewEngine } from './computeDisabilityDaysSummaryNewEngine'
import Icon from '@jimmyboys/liita-ui/dist/components/Icon'

const DisabilityDaysSummary = (props) => {
	const { summary } = props
	const { i18n } = useGlobal()

	return summary.map((item) => (
		<div
			key={`${item.rate}-${item.disabilityId}`}
			className={styles['disability-rate']}
		>
			<small>
				<strong>{item.rate}%</strong> ({item.disabilityId}) -{' '}
				{item.count} {i18n`day`}
				{item.count !== 1 && 's'}
			</small>
		</div>
	))
}

const expandExpertiseSegments = (periods) => {
	const expanded = []

	periods.forEach((period) => {
		if (!period.expertiseSegments || period.expertiseSegments.length <= 1) {
			expanded.push(period)
			return
		}

		period.expertiseSegments.forEach((segment, index) => {
			const daysCount = segment.endDate
				? moment(segment.endDate).diff(
						moment(segment.startDate),
						'days'
				  ) + 1
				: null

			expanded.push({
				...period,
				id: `${period.id}-${index}`,
				expertiseId: segment.expertiseId,
				expertise: segment.expertise,
				startDate: segment.startDate,
				endDate: segment.endDate,
				daysCount,
			})
		})
	})

	return expanded
}

const groupByExpertise = (periods) => {
	const groups = []

	expandExpertiseSegments(periods).forEach((period) => {
		let group = groups.find((g) => g.expertiseId === period.expertiseId)

		if (!group) {
			group = {
				expertiseId: period.expertiseId,
				expertise: period.expertise,
				periods: [],
			}
			groups.push(group)
		}

		group.periods.push(period)
	})

	return groups
}

const getClinicalEpisodeRange = (periods) => {
	if (!periods.length) return null

	let start = null
	let end = null

	periods.forEach((period) => {
		const periodStart = moment(period.startDate)
		const periodEnd = period.endDate ? moment(period.endDate) : moment()

		if (!start || periodStart.isBefore(start)) start = periodStart
		if (!end || periodEnd.isAfter(end)) end = periodEnd
	})

	return { start, end }
}

const ClinicalEpisodeTitle = (props) => {
	const { range } = props
	const { i18n } = useGlobal()

	if (!range) return null

	const start = parseDate(range.start.toDate())
	const end = parseDate(range.end.toDate())

	return (
		<div className={styles['clinical-episode-title']}>
			<strong>{i18n`Clinical episode`}</strong>{' '}
			<small>
				({start.shortDateFormat} - {end.shortDateFormat})
			</small>
		</div>
	)
}

const getTodayDisability = (periods) => {
	const today = moment().startOf('day')

	const current = (periods ?? []).find((period) => {
		const start = moment(period.startDate).startOf('day')
		const end = period.endDate
			? moment(period.endDate).startOf('day')
			: null

		return today.isSameOrAfter(start) && (!end || today.isSameOrBefore(end))
	})

	return current ? `${current.rate}% (${current.disabilityId})` : undefined
}

const DisabilitiesNewEngineWidget = (props) => {
	const { data } = props
	const { disabilitiesNewEngine } = data
	const { i18n } = useGlobal()
	const params = useParams()
	const processId = params.clinicalProcessId
	const periods = disabilitiesNewEngine ?? []
	const todaysDisability = getTodayDisability(periods)
	const groups = groupByExpertise(periods)
	const daysSummary = computeDisabilityDaysSummaryNewEngine(periods)
	const episodeRange = getClinicalEpisodeRange(periods)

	return (
		<ExpandableWidget
			className={styles['aside-widget']}
			titleAppearance='aside-widget'
			arrowPosition='right'
			heading='h5'
			icon='disabled'
			title={i18n`Disabilities (new engine)`}
			expanded={!!periods.length}
			expandable={!!periods.length}
			borderRadius
			margins={['small', '', '']}
			paddings={['xsmall', 'xsmall', 'xsmall']}
		>
			<div className={styles['today-disability-rate']}>
				<small>
					<strong>{i18n`Current disability`}: </strong>{' '}
					{todaysDisability ??
						i18n`Without data to determine disability.`}
				</small>
			</div>
			<ClinicalEpisodeTitle range={episodeRange} />
			<DisabilityDaysSummary summary={daysSummary} />
			<VStack gap='small'>
				{groups.map((group) => (
					<VStack
						key={`new_engine_expertise_${group.expertiseId}`}
						className={styles['expertise-disability-entry']}
						gap='small'
					>
						<h5 className={styles['group-title']}>
							{group.expertise}
						</h5>
						{group.periods.map((period) => {
							const startDate = parseDate(period.startDate)
							const endDate = period.endDate
								? parseDate(period.endDate)
								: null

							return (
								<HStack
									key={period.id}
									className={styles['disability-entry']}
									gap='small'
									align='center'
								>
									<div
										className={
											styles[
												'circular-progress-container'
											]
										}
									>
										<CircularProgress
											size={20}
											progress={period.rate}
										/>
										<small
											className={
												styles[
													'circular-progress-percentage'
												]
											}
										>
											{period.rate}
										</small>
										<small>%</small>
									</div>
									<div>
										<h5>{period.disability}</h5>
										<small>
											{startDate.shortDateFormat} -{' '}
											{endDate?.shortDateFormat || ''} (
											{period.daysCount ?? '-'}{' '}
											{i18n`day`}
											{period.daysCount !== 1 && 's'})
										</small>
									</div>
								</HStack>
							)
						})}
					</VStack>
				))}
				<HStack justify='flex-end'>
					<Link
						to={`/clinical-processes/${processId}/disabilities`}
						className={styles['check-disabilities']}
					>
						<Icon name='branch' width='20px' height='20px' />
						{i18n`Check`}
					</Link>
				</HStack>
			</VStack>
		</ExpandableWidget>
	)
}

export default DisabilitiesNewEngineWidget
